/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { forwardRef, ForwardedRef } from 'preact/compat';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLDivElement>,
  'id' | 'class' | 'children'
>;
type WrapperProps = IntrinsicProps & {
  color?: string;
  borderWidth?: string;
  ariaProps?: {
    'aria-valuenow'?: string;
    'aria-valuemin'?: string;
    'aria-valuemax'?: string;
    'aria-valuetext'?: string;
    'aria-label'?: string;
    role?: string;
  };
  clipPath?: string;
};

export const CircleWrapper = forwardRef(
  (
    { id, children, color, clipPath, borderWidth, ariaProps, ...otherProps }: WrapperProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <div
        id={id}
        ref={ref}
        {...ariaProps}
        class={otherProps.class}
        style={{
          borderColor: color,
          borderWidth: borderWidth,
          clipPath: clipPath
        }}>
        {children}
      </div>
    );
  }
);

type CircleInnerProps = Pick<IntrinsicProps, 'class'> & {
  clipPath?: string;
  color?: string;
  width?: string;
  height?: string;
  size?: string;
  section?: string;
};

function getBorderWidth(section: string, borderWidth: string | undefined) {
  const border: Record<string, string | undefined> = {
    borderTopWidth: borderWidth,
    borderBottomWidth: borderWidth,
    borderRightWidth: borderWidth,
    borderLeftWidth: borderWidth
  };
  if (section === 'top') {
    border['borderBottomWidth'] = undefined;
  }

  if (section === 'bottom') {
    border['borderTopWidth'] = undefined;
  }

  if (section === 'left') {
    border['borderRightWidth'] = undefined;
  }

  if (section === 'right') {
    border['borderLeftWidth'] = undefined;
  }
  return border;
}

export const CircleInner = ({
  clipPath,
  color,
  height,
  width,
  size,
  section = 'full',
  ...otherProps
}: CircleInnerProps) => {
  const borderWidth = getBorderWidth(section, size);
  return (
    <div
      class={otherProps.class}
      style={{
        clipPath: clipPath,
        borderColor: color,
        height: height,
        width: width,
        ...borderWidth
      }}></div>
  );
};
